import React from 'react';
import { Card, CardContent } from './ui/card';
import { Shield, Zap, TrendingUp, TrendingDown } from 'lucide-react';

const ScoreCard = ({ type, score, color, size = 'normal', showBreakdown = false, breakdown = {} }) => {
  const getColorClasses = (colorName) => {
    switch(colorName) {
      case 'green':
        return {
          bg: 'bg-green-100',
          text: 'text-green-700',
          border: 'border-green-300',
          ring: 'ring-green-500'
        };
      case 'amber':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-700',
          border: 'border-yellow-300',
          ring: 'ring-yellow-500'
        };
      case 'red':
        return {
          bg: 'bg-red-100',
          text: 'text-red-700',
          border: 'border-red-300',
          ring: 'ring-red-500'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          border: 'border-gray-300',
          ring: 'ring-gray-500'
        };
    }
  };

  const colors = getColorClasses(color);
  const Icon = type === 'security' ? Shield : Zap;
  const isSmall = size === 'small';
  const displayScore = score === null || score === undefined ? 'N/A' : score;
  const hasScore = score !== null && score !== undefined;

  if (isSmall) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${colors.border} ${colors.bg}`}>
        <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
        <span className={`text-base font-bold ${colors.text}`}>{displayScore}</span>
      </div>
    );
  }

  return (
    <Card className={`border-2 ${colors.border} ${colors.bg} transition-shadow hover:shadow-md`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${colors.text}`} />
            </div>
            <div>
              <h3 className={`text-sm font-semibold ${colors.text} uppercase tracking-wide`}>
                {type === 'security' ? 'Security Score' : 'Performance Score'}
              </h3>
              <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-3xl font-bold ${colors.text}`}>{displayScore}</span>
                {hasScore && <span className={`text-sm ${colors.text} opacity-70`}>/ 100</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {hasScore ? (
          <>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  color === 'green' ? 'bg-green-500' :
                  color === 'amber' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${score}%` }}
              />
            </div>

            <div className={`text-xs ${colors.text} opacity-80`}>
              {color === 'green' && 'Excellent condition'}
              {color === 'amber' && 'Attention needed'}
              {color === 'red' && 'Critical issues detected'}
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-500 opacity-80 mt-2">
            Waiting for agent data...
          </div>
        )}

        {showBreakdown && breakdown && Object.keys(breakdown).length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs font-semibold text-gray-600 mb-2">Score Breakdown:</p>
            <div className="space-y-1">
              {Object.entries(breakdown).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center text-xs">
                  <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                  <span className="font-medium text-gray-700">{Math.round(value * 10) / 10}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScoreCard;
